import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import type { AuthSession, AuthUser, LoginOptions } from "./session";

// =============================================================================
// Anmeldezustand für React-Komponenten und den Router
// =============================================================================
// Ein React-Context stellt einen Wert allen Komponenten darunter bereit, ohne
// ihn durch jede Ebene als Prop durchzureichen. Hier: Session + aktueller Benutzer.

// "Discriminated Union": `status` entscheidet, welche Felder es gibt. Nach
// `if (state.status === "authenticated")` kennt TypeScript auch `state.user`.
export type AuthState =
  { readonly status: "anonymous" } | { readonly status: "authenticated"; readonly user: AuthUser };

export interface AuthContextValue {
  readonly state: AuthState;
  // Immer der neueste Stand, auch direkt nach login() im selben Ablauf.
  // Der Router nutzt das in seinen Prüfungen (beforeLoad), weil sein Kontext
  // erst beim nächsten Rendern aktualisiert würde.
  getState(): AuthState;
  readonly session: AuthSession;
  login(options: LoginOptions): Promise<void>;
  // Nach /callback: Benutzer übernehmen, ohne erneut zu laden.
  setUser(user: AuthUser): void;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  readonly session: AuthSession;
  // Ergebnis von session.restore() beim Start (main.tsx).
  readonly initialUser: AuthUser | null;
  readonly children: ReactNode;
}

export function AuthProvider({ session, initialUser, children }: AuthProviderProps) {
  const initial: AuthState = initialUser
    ? { status: "authenticated", user: initialUser }
    : { status: "anonymous" };
  const [state, setState] = useState<AuthState>(initial);
  // useRef: ein veränderbarer Behälter, dessen Änderung KEIN Neu-Rendern
  // auslöst. Hält den Stand synchron für getState().
  const stateRef = useRef<AuthState>(initial);

  // useMemo: dasselbe Objekt, solange sich state nicht ändert – sonst würden
  // alle Konsumenten bei jedem Rendern unnötig neu rendern.
  const value = useMemo<AuthContextValue>(() => {
    function update(next: AuthState): void {
      stateRef.current = next;
      setState(next);
    }
    return {
      state,
      getState: () => stateRef.current,
      session,
      async login(options) {
        const user = await session.login(options);
        update({ status: "authenticated", user });
      },
      setUser(user) {
        update({ status: "authenticated", user });
      },
      async logout() {
        // Bewusst KEIN Zwischenzustand „abgemeldet“ vor der Weiterleitung:
        // Er ließ die Seite neu rendern und ohne Token nachladen (HTTP 401 auf
        // der aktuellen Seite), während der Browser gleichzeitig zu Cognito
        // wechseln sollte. session.logout() verlässt die Seite direkt; der
        // Neustart der App beginnt ohnehin abgemeldet und mit leerem Cache.
        await session.logout();
      },
    };
  }, [state, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook für Komponenten. Wirft, wenn er außerhalb des Providers benutzt wird –
// ein Programmierfehler, der sofort auffallen soll.
export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth() außerhalb von <AuthProvider> verwendet");
  return value;
}
